import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Bill } from "@shared/operations-schema";
import { useBills } from "@/hooks/use-bills";
import { useBillPayments } from "@/hooks/use-bill-payments";

// Define the schema for the form
const billPaymentSchema = z.object({
  billId: z.string().min(1, "Please select a bill"),
  amount: z.string().min(1, "Please enter an amount"),
  paymentDate: z.date({ required_error: "Please select a payment date" }),
  paymentMethod: z.string().min(1, "Please select a payment method"),
  referenceNumber: z.string().optional(),
  notes: z.string().optional(),
});

type BillPaymentFormValues = z.infer<typeof billPaymentSchema>;

interface CreateBillPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: number;
}

export function CreateBillPaymentDialog({
  isOpen,
  onClose,
  businessId,
}: CreateBillPaymentDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { bills, isLoading: isLoadingBills } = useBills(businessId);

  // Filter out paid bills
  const unpaidBills = bills?.filter(bill => bill.status !== 'paid') || [];

  // Set up form with default values
  const form = useForm<BillPaymentFormValues>({
    resolver: zodResolver(billPaymentSchema),
    defaultValues: {
      billId: "",
      amount: "",
      paymentDate: new Date(),
      paymentMethod: "",
      referenceNumber: "",
      notes: "",
    },
  });

  // Use the hook's mutation for creating bill payment
  const { createBillPaymentMutation } = useBillPayments();

  // Handle form submission
  const onSubmit = (data: BillPaymentFormValues) => {
    createBillPaymentMutation.mutate({
      billId: parseInt(data.billId),
      payment_date: data.paymentDate,
      amount: data.amount,
      paymentMethod: data.paymentMethod,
      reference_number: data.referenceNumber,
      notes: data.notes
    });
    
    onClose();
    form.reset();
  };

  // Find the selected bill to auto-fill the amount
  const handleBillChange = (billId: string) => {
    const selectedBill = unpaidBills.find(bill => bill.id === parseInt(billId));
    if (selectedBill) {
      form.setValue("amount", selectedBill.amount);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Bill Payment</DialogTitle>
          <DialogDescription>
            Enter the details of the payment you made for a bill.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Bill selection */}
            <FormField
              control={form.control}
              name="billId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bill</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleBillChange(value);
                    }}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a bill" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingBills ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : unpaidBills.length > 0 ? (
                        unpaidBills.map((bill) => (
                          <SelectItem key={bill.id} value={bill.id.toString()}>
                            {bill.billNumber || `Bill #${bill.id}`} - ${bill.amount} ({bill.status})
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-center">
                          <p className="text-sm text-muted-foreground mb-2">No unpaid bills available</p>
                          <div className="mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                onClose();
                                // Navigate to bills page
                                window.location.href = '/expenses/bills';
                              }}
                            >
                              Create Bill
                            </Button>
                          </div>
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount ($)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="0.00"
                      {...field}
                      type="number"
                      step="0.01"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment date */}
            <FormField
              control={form.control}
              name="paymentDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Payment Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment method */}
            <FormField
              control={form.control}
              name="paymentMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Reference number */}
            <FormField
              control={form.control}
              name="referenceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference Number (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. check number, confirmation code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any additional notes about this payment"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Footer buttons */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createBillPaymentMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createBillPaymentMutation.isPending}>
                {createBillPaymentMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
